import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { StaffRoleGuard, STAFF_PERMISSION_FALLBACK_ROUTE_DATA, STAFF_PERMISSION_ROUTE_DATA } from '@wsphere/staff/web';
import { ClaimModule } from './claim.module';
import {
  ClaimItemAdjudicateComponent,
  ClaimItemCureComponent,
  ClaimItemDetailsComponent,
  ClaimItemResolveComponent,
} from './components';
import {
  ClaimItemAdjudicateDialog,
  ClaimItemCureDialog,
  ClaimItemDetailsDialog,
  ClaimItemDialog,
  ClaimItemResolveDialog,
  ManagePaymentDialog,
} from './dialogs';
import {
  ClaimGuard,
  ClaimItemAdjudicationGuard,
  ClaimItemGuard,
  ClaimItemManageGuard,
  ClaimPaymentGuard,
} from './guards';
import {
  ClaimAssetPage,
  ClaimAttachmentsPage,
  ClaimAuditPage,
  ClaimNotesPage,
  ClaimOverviewPage,
  ClaimPage,
  ClaimPaymentsPage,
  ClaimsPage,
  ClaimWorkspacePage,
} from './pages';
import { ClaimItemResolver, ClaimPaymentResolver, ClaimResolver } from './resolver';

/** Job dialog routes, shared by the claim page and the workspace. */
const CLAIM_ITEM_ROUTES: Routes = [
  {
    path: ':itemId',
    component: ClaimItemDialog,
    canActivate: [ClaimItemGuard, ClaimGuard],
    resolve: { item: ClaimItemResolver },
    children: [
      {
        path: '',
        component: ClaimItemDetailsComponent,
        resolve: { item: ClaimItemResolver },
      },
      {
        path: 'cure',
        component: ClaimItemCureComponent,
        resolve: { item: ClaimItemResolver },
      },
      {
        path: 'adjudicate',
        component: ClaimItemAdjudicateComponent,
        resolve: { item: ClaimItemResolver },
      },
      {
        path: 'resolve',
        component: ClaimItemResolveComponent,
        canActivate: [ClaimItemGuard, ClaimGuard, ClaimItemAdjudicationGuard],
        resolve: { item: ClaimItemResolver },
      },
    ],
  },
  {
    path: ':itemId/manage-details',
    component: ClaimItemDetailsDialog,
    data: {
      [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ManageClaimItemCoverage',
      [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/:itemId' }],
    },
    canActivate: [ClaimItemGuard, StaffRoleGuard, ClaimItemManageGuard],
    resolve: { item: ClaimItemResolver },
  },
  {
    path: ':itemId/manage-cure',
    component: ClaimItemCureDialog,
    data: {
      [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ManageClaimItemCure',
      [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/:itemId' }],
    },
    canActivate: [ClaimItemGuard, StaffRoleGuard, ClaimItemManageGuard],
    resolve: { item: ClaimItemResolver, claim: ClaimResolver },
  },
  {
    path: ':itemId/manage-adjudication',
    component: ClaimItemAdjudicateDialog,
    data: {
      [STAFF_PERMISSION_ROUTE_DATA]: 'clm_AdjudicateClaimItem',
      [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/:itemId' }],
    },
    canActivate: [ClaimItemGuard, StaffRoleGuard, ClaimItemManageGuard],
    resolve: { item: ClaimItemResolver },
  },
  {
    path: ':itemId/manage-resolution',
    component: ClaimItemResolveDialog,
    data: {
      [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ManageClaimItemResolution',
      [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/:itemId' }],
    },
    canActivate: [ClaimItemGuard, StaffRoleGuard, ClaimItemManageGuard],
    resolve: { item: ClaimItemResolver, claim: ClaimResolver },
  },
];

const routes: Routes = [
  {
    path: '',
    data: {
      [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ViewClaim',
      [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [
        { permissionId: 'dsh_ViewDashboard', route: '' },
        { permissionId: 'pol_View', route: 'policies' },
        { permissionId: 'py_ViewSales', route: 'sales' },
      ],
    },
    canActivate: [StaffRoleGuard],
    component: ClaimsPage,
  },
  {
    path: ':claimId/workspace',
    component: ClaimWorkspacePage,
    data: {
      [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ViewClaim',
      [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/claims' }],
    },
    canActivate: [ClaimGuard, StaffRoleGuard],
    resolve: { claim: ClaimResolver },
    children: CLAIM_ITEM_ROUTES,
  },
  {
    path: ':claimId',
    component: ClaimPage,
    data: {
      [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ViewClaim',
      [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/claims' }],
    },
    canActivate: [ClaimGuard, StaffRoleGuard],
    resolve: { claim: ClaimResolver },

    children: [
      {
        path: 'asset',
        component: ClaimAssetPage,
        data: {
          [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ViewClaim',
          [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/claims' }],
        },
        canActivate: [StaffRoleGuard],
      },
      {
        path: 'notes',
        component: ClaimNotesPage,
        data: {
          [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ViewNote',
          [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/claims' }],
        },
        canActivate: [StaffRoleGuard],
      },
      {
        path: 'attachments',
        component: ClaimAttachmentsPage,
        data: {
          [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ViewAttachments',
          [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/claims' }],
          isCustomerPortal: false,
        },
        canActivate: [StaffRoleGuard],
      },
      {
        path: 'audit',
        component: ClaimAuditPage,
        data: {
          [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ViewAudit',
          [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/claims' }],
        },
        canActivate: [StaffRoleGuard],
      },
      {
        path: 'payments',
        component: ClaimPaymentsPage,
        data: {
          [STAFF_PERMISSION_ROUTE_DATA]: 'clm_ViewAttachments',
          [STAFF_PERMISSION_FALLBACK_ROUTE_DATA]: [{ permissionId: 'clm_ViewClaim', route: '/claims' }],
        },
        canActivate: [StaffRoleGuard],
        children: [
          {
            path: ':paymentId',
            component: ManagePaymentDialog,
            canActivate: [ClaimPaymentGuard],
            resolve: { payment: ClaimPaymentResolver },
          },
        ],
      },
      {
        path: '',
        component: ClaimOverviewPage,
        children: CLAIM_ITEM_ROUTES,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes), ClaimModule],
})
export class ClaimRoutingModule {}
